import el from "@cypherpotato/el";
import ApexCharts from "apexcharts";
import page from "page";
import { Button, ButtonRow } from "../../components/button";
import { Card, CardContent, CardGrid, CardHeader } from "../../components/card";
import { DailyUsageWidget } from "../../components/daily-usage-widget";
import { Dashboard, DashboardTitle } from "../../components/dashboard";
import { Dropdown, DropdownItem } from "../../components/dropdown";
import { Select } from "../../components/form";
import { Icon } from "../../components/icon";
import { Section } from "../../components/layout";
import { ExternalLink } from "../../components/link";
import { BlockCode, Paragraph } from "../../components/text";
import { themePreference } from "../../global-state";
import { setManagedInterval } from "../../interval-manager";
import { runOperation } from "../../ui/operation";
import { cond } from "../../util";
import { api } from './../../api';
import { copyToClipboard } from "../../ui/clipboard";
import { displayAlert, displayFinder } from "../../ui/alert";

const snippetOptions = [
    { value: "rag", text: "RAG query" },
    { value: "chat", text: "Chat completions" },
    { value: "structured", text: "Structured JSON" }
];

export default function (ctx) {

    const dashboard = el.state.create(null);
    const collections = el.state.create([]);
    const apiKeys = el.state.create([]);
    const gateways = el.state.create([]);
    const snippetType = el.state.create("rag");
    const snippetSample = el.state.create({
        collectionId: "<COLLECTION_ID>",
        collectionName: "Knowledge base",
        modelName: "@openai/gpt-5-mini",
        apiKey: "sk-aiv-acc...",
        tick: 0
    });

    function pickRandom(items, fallback) {
        const usableItems = items?.length ? items : [fallback];
        return usableItems[Math.floor(Math.random() * usableItems.length)];
    }

    function copySetupPrompt(type) {
        displayAlert({
            title: "Warning",
            dialogSize: "small",
            message: el.fragment(
                Paragraph("This prompt gives your agent total control over your account. Only paste the prompt in a safe and trusted environment."),
                Paragraph("Do you want to continue?")
            ),
            type: "alert-warning",
            buttons: [
                {
                    label: "Cancel"
                },
                {
                    label: "Yes, continue",
                    class: ["primary"],
                    onClick() {
                        runOperation("Getting agent setup prompt...", async () => {
                            const prompt = await api.getJson(`/api/v1/accounts/agent-setup-prompt?type=${type}`);
                            copyToClipboard(prompt.data.setupPrompt);
                        });
                    }
                }
            ]
        });
    }

    function updateSnippetSample() {
        const availableModelNames = dashboard.value?.availableModels
            ?.flatMap(provider => provider.models?.map(model => model.name) || [])
            ?.filter(Boolean) || [];
        const gatewayModelNames = gateways.value
            ?.flatMap(gateway => [gateway.slug, gateway.modelName])
            ?.filter(Boolean) || [];
        const collection = pickRandom(collections.value, {
            id: "<COLLECTION_ID>",
            name: "Knowledge base"
        });
        const apiKey = pickRandom(
            apiKeys.value?.filter(key => key.type !== "public") || apiKeys.value,
            { key: "sk-aiv-acc..." }
        );

        snippetSample.value = {
            collectionId: collection.id,
            collectionName: collection.name,
            modelName: pickRandom([...gatewayModelNames, ...availableModelNames], "@openai/gpt-5-mini"),
            apiKey: apiKey.key,
            tick: snippetSample.value.tick + 1
        };
    }

    runOperation(null, async () => {
        const [result, collectionsResult, apiKeysResult, gatewaysResult] = await Promise.all([
            api.getJson("/api/v1/dashboard"),
            api.getJson("/api/v1/collections"),
            api.getJson("/api/v1/accounts/api-key"),
            api.getJson("/api/v1/ai-gateways")
        ]);

        dashboard.value = result.data;
        collections.value = collectionsResult.data || [];
        apiKeys.value = apiKeysResult.data?.apiKeys || [];
        gateways.value = gatewaysResult.data || [];
        updateSnippetSample();
    });

    function getPlots() {
        if (!dashboard.value?.plots.length)
            return [];

        return dashboard.value.plots.map(plot => {
            const plotTarget = el('.dashboard-plot');
            const chartOptions = {
                series: [
                    {
                        name: plot.plotName,
                        data: plot.items.map(item => [
                            item.label,
                            Math.round(item.value * 100) / 100
                        ])
                    }
                ],
                chart: {
                    type: "area",
                    height: 350,
                    zoom: {
                        autoScaleYaxis: true
                    },
                    toolbar: {
                        show: false,
                    }
                },
                tooltip: {
                    theme: themePreference.value
                },
                dataLabels: {
                    enabled: false
                },
                xaxis: {
                    type: "datetime",
                    tickAmount: 10
                },
                colors: ['#007bff'],
                fill: {
                    type: 'gradient',
                    gradient: {
                        shadeIntensity: .8,
                        opacityFrom: .7,
                        opacityTo: .4,
                        stops: [0, 80, 100]
                    }
                },
                stroke: {
                    show: true,
                    width: 2
                }
            }

            const chart = new ApexCharts(plotTarget, chartOptions);
            el.detectMount(plotTarget, () => chart.render());

            return { target: plotTarget, plot };
        });
    }

    return el.state.listening([dashboard, themePreference], () => {
        return Dashboard(
            DashboardTitle("Dashboard", "Welcome to your AIVAX dashboard"),
            Section(
                Card(
                    {
                        classList: ["bg-card"]
                    },
                    CardContent(
                        el(".dashboard-presentation",
                            el(".dashboard-presentation-copy",
                                el("h2", "What are we going to build today?"),
                                el("p",
                                    "Read our ",
                                    ExternalLink("getting started guide", "https://docs.aivax.net/docs/getting-started"),
                                    " to start developing intelligent applications with AIVAX."
                                ),
                                ButtonRow(
                                    Button(
                                        "Get started", Icon("arrow-right-line"),
                                        {
                                            onClick() {
                                                window.open("https://docs.aivax.net/docs/getting-started", "_blank");
                                            }
                                        }
                                    ),
                                    Dropdown(
                                        el("span",
                                            Icon("bard-fill"), "Copy Prompt",
                                            { title: "Copy an initial setup prompt for agents like Claude Code, Codex and others" }
                                        ),
                                        DropdownItem("Agent MCP + Skills", Icon("plug-line"), {
                                            onClick: () => copySetupPrompt("mcp")
                                        }),
                                        DropdownItem("Agent CLI", Icon("terminal-box-line"), {
                                            onClick: () => copySetupPrompt("cli")
                                        })
                                    )
                                )
                            ),
                            el(".dashboard-presentation-visuals",
                                el.state.listening([snippetType, snippetSample], () => {
                                    const sample = snippetSample.value;
                                    const snippets = {
                                        rag:
                                            "curl https://inference.aivax.net/api/v1/query \\\n" +
                                            `  -H 'Authorization: Bearer ${sample.apiKey}' \\\n` +
                                            "  -H 'Content-Type: application/json' \\\n" +
                                            "  -d '{\n" +
                                            "    \"terms\": [\"What should I know before onboarding a customer?\"],\n" +
                                            `    \"collections\": [\"${sample.collectionId}\"],\n` +
                                            "    \"top\": 5,\n" +
                                            "    \"minScore\": 0.4,\n" +
                                            "    \"includeReferences\": true\n" +
                                            "  }'",
                                        chat:
                                            "curl https://inference.aivax.net/v1/chat/completions \\\n" +
                                            `  -H 'Authorization: Bearer ${sample.apiKey}' \\\n` +
                                            "  -H 'Content-Type: application/json' \\\n" +
                                            "  -d '{\n" +
                                            `    \"model\": \"${sample.modelName}\",\n` +
                                            "    \"messages\": [\n" +
                                            "      { \"role\": \"user\", \"content\": \"Write a concise welcome message.\" }\n" +
                                            "    ]\n" +
                                            "  }'",
                                        structured:
                                            "curl https://inference.aivax.net/v1/chat/completions \\\n" +
                                            `  -H 'Authorization: Bearer ${sample.apiKey}' \\\n` +
                                            "  -H 'Content-Type: application/json' \\\n" +
                                            "  -d '{\n" +
                                            `    \"model\": \"${sample.modelName}\",\n` +
                                            "    \"messages\": [\n" +
                                            "      { \"role\": \"user\", \"content\": \"Extract a support ticket summary and priority.\" }\n" +
                                            "    ],\n" +
                                            "    \"response_format\": {\n" +
                                            "      \"type\": \"json_schema\",\n" +
                                            "      \"json_schema\": {\n" +
                                            "        \"schema\": {\n" +
                                            "          \"type\": \"object\",\n" +
                                            "          \"additionalProperties\": false,\n" +
                                            "          \"required\": [\"summary\", \"priority\"],\n" +
                                            "          \"properties\": {\n" +
                                            "            \"summary\": { \"type\": \"string\" },\n" +
                                            "            \"priority\": { \"type\": \"string\", \"enum\": [\"low\", \"medium\", \"high\"] }\n" +
                                            "          }\n" +
                                            "        }\n" +
                                            "      }\n" +
                                            "    }\n" +
                                            "  }'"
                                    };

                                    return el(".dashboard-snippet-panel",
                                        el(".dashboard-snippet-toolbar",
                                            Select({
                                                value: snippetType.value,
                                                items: snippetOptions,
                                                title: "Example type",
                                                onChange(event) {
                                                    snippetType.value = event.target.value;
                                                    updateSnippetSample();
                                                }
                                            })
                                        ),
                                        el(".dashboard-snippet-code",
                                            { classList: [`snippet-tick-${sample.tick % 2}`] },
                                            BlockCode(snippets[snippetType.value], "bash")
                                        )
                                    );
                                })
                            )
                        )
                    )
                )
            ),
            Section(
                CardGrid(
                    {
                        classList: ["span"]
                    },
                    dashboard.value?.cards.map(card =>
                        Card({ classList: ["bg-card"] },
                            CardHeader(card.topLabel),
                            CardContent(
                                {
                                    classList: ["dashboard-card-content"]
                                },
                                el("h3", card.headingLabel),
                                card.detail && el.fragment(
                                    el("p",
                                        el("strong", card.detail.detailTitle)
                                    ),
                                    el("p", card.detail.detailSubTitle)
                                ),
                                card.clickAction && cond([
                                    [card.clickAction.type === "internal", { onClick() { page(card.clickAction.parameter) } }]
                                ])
                            )
                        )
                    )
                ),
            ),
            Section(
                DailyUsageWidget(dashboard.value?.dailyTokenUsage || []),
            ),
            Section(
                getPlots().map(plot =>
                    Card({ classList: "bg-card" },
                        CardHeader(plot.plot.plotName),
                        CardContent(plot.target)
                    )
                )
            )
        );
    })
}
